'use client';

import type { ReactNode } from 'react';
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../utils/cx';
import { Avatar } from './avatar';
import { Text } from './text';
import { Tooltip } from './tooltip';

export interface NavUserMenuProps {
  userName?: string;
  userSubLabel?: string;
  isGuest?: boolean;
  avatarSeed?: string;
  avatarSize?: number;
  tooltip?: ReactNode;
  triggerAriaLabel?: string;
  menuAriaLabel?: string;
  closeOnRouteChange?: boolean;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
  children?: ReactNode;
}

type OpenInteraction = 'pointer' | 'keyboard';

type MenuPosition = {
  top: number;
  left: number;
  placement: 'right' | 'left';
};

const EXIT_ANIMATION_MS = 200;

function getLocationKey(): string {
  if (typeof window === 'undefined') return '';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/**
 * Framework-agnostic route change detection:
 * - popstate/hashchange (back/forward + hash)
 * - monkey-patch history.pushState/replaceState (SPA navigations, including Next)
 */
function useRouteKey(enabled: boolean): string {
  const [key, setKey] = useState(() => (enabled ? getLocationKey() : ''));

  useEffect(() => {
    if (!enabled) return;

    const update = () => setKey(getLocationKey());

    const onPop = () => update();
    const onHash = () => update();

    window.addEventListener('popstate', onPop);
    window.addEventListener('hashchange', onHash);

    const historyRef = window.history as History & {
      __cui_patched__?: boolean;
      __cui_push__?: History['pushState'];
      __cui_replace__?: History['replaceState'];
    };

    if (!historyRef.__cui_patched__) {
      historyRef.__cui_patched__ = true;

      historyRef.__cui_push__ = historyRef.pushState;
      historyRef.__cui_replace__ = historyRef.replaceState;

      historyRef.pushState = function pushStatePatched(...args) {
        historyRef.__cui_push__!.apply(this, args as any);
        window.dispatchEvent(new Event('cui:locationchange'));
      };

      historyRef.replaceState = function replaceStatePatched(...args) {
        historyRef.__cui_replace__!.apply(this, args as any);
        window.dispatchEvent(new Event('cui:locationchange'));
      };
    }

    window.addEventListener('cui:locationchange', update);

    update();

    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('cui:locationchange', update);
    };
  }, [enabled]);

  return key;
}

export function NavUserMenu({
  userName,
  userSubLabel,
  isGuest = false,
  avatarSeed,
  avatarSize = 38,
  tooltip,
  triggerAriaLabel,
  menuAriaLabel = 'User menu',
  children,
  className,
  triggerClassName,
  menuClassName,
  closeOnRouteChange = true,
}: NavUserMenuProps) {
  const reactId = useId();
  const routeKey = useRouteKey(Boolean(closeOnRouteChange));

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const openInteractionRef = useRef<OpenInteraction>('pointer');
  const closeTimerRef = useRef<number | null>(null);

  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const [hasMounted, setHasMounted] = useState(false);

  const menuId = useMemo(() => `nav-user-menu-${reactId}`, [reactId]);

  const primary = (userName ?? '').trim() || 'Account';
  const secondaryRaw = (userSubLabel ?? '').trim();
  const secondary =
    secondaryRaw && secondaryRaw !== primary
      ? secondaryRaw
      : isGuest
        ? `${primary} (guest)`
        : '';

  const derivedTooltip = tooltip ?? (primary ? `${primary}${isGuest ? ' (guest)' : ''}` : 'Account');

  const seed = useMemo(() => {
    const s = (avatarSeed ?? '').trim();
    if (s) return s;
    const u = (userName ?? '').trim();
    if (u) return u;
    return 'account';
  }, [avatarSeed, userName]);

  const avatarSrc = useMemo(() => {
    const encoded = encodeURIComponent(seed);
    return `/api/avatar/${encoded}.svg?size=${encodeURIComponent(String(avatarSize))}&variant=beam&round=true`;
  }, [avatarSize, seed]);

  useEffect(() => setHasMounted(true), []);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  }, []);

  const closeMenu = (opts?: { restoreFocus?: boolean }) => {
    setOpen(false);
    if (opts?.restoreFocus) triggerRef.current?.focus?.({ preventScroll: true });
  };

  const openMenu = (interaction: OpenInteraction) => {
    openInteractionRef.current = interaction;
    setOpen(true);
  };

  useEffect(() => {
    if (!closeOnRouteChange) return;
    if (!open) return;
    // routeKey change => close
    closeMenu({ restoreFocus: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setRenderMenu(true);
      setIsVisible(false);
      return;
    }

    setIsVisible(false);

    if (renderMenu) {
      closeTimerRef.current = window.setTimeout(() => {
        setRenderMenu(false);
        setPosition(null);
      }, EXIT_ANIMATION_MS);
    }
  }, [open, renderMenu]);

  const updatePosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const gap = 8;
    const viewportPadding = 8;

    const t = trigger.getBoundingClientRect();
    const m = menu.getBoundingClientRect();

    let placement: MenuPosition['placement'] = 'right';
    let left = t.right + gap;

    if (left + m.width > window.innerWidth - viewportPadding) {
      placement = 'left';
      left = t.left - gap - m.width;
    }

    let top = t.bottom - m.height;

    left = Math.max(viewportPadding, Math.min(left, window.innerWidth - m.width - viewportPadding));
    top = Math.max(viewportPadding, Math.min(top, window.innerHeight - m.height - viewportPadding));

    const next: MenuPosition = { top, left, placement };

    setPosition((prev) => {
      if (!prev) return next;
      if (prev.top === next.top && prev.left === next.left && prev.placement === next.placement) return prev;
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    if (!renderMenu) return;

    updatePosition();

    const onResize = () => updatePosition();
    const onScroll = () => updatePosition();

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, renderMenu, updatePosition]);

  useEffect(() => {
    if (!open) return;
    if (!renderMenu) return;
    if (!position) return;
    if (isVisible) return;

    const raf = window.requestAnimationFrame(() => setIsVisible(true));
    return () => window.cancelAnimationFrame(raf);
  }, [open, renderMenu, position, isVisible]);

  useEffect(() => {
    if (!open) return;

    const isEventInside = (target: Node | null) => {
      if (!target) return false;
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      return Boolean(trigger?.contains(target) || menu?.contains(target));
    };

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (isEventInside(target)) return;
      closeMenu({ restoreFocus: false });
    };

    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as Node | null;
      if (isEventInside(target)) return;
      closeMenu({ restoreFocus: false });
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu({ restoreFocus: true });
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (openInteractionRef.current !== 'keyboard') return;

    const raf = window.requestAnimationFrame(() => {
      const menu = menuRef.current;
      if (!menu) return;

      const focusable = menu.querySelector<HTMLElement>(
        [
          '[role="menuitem"]',
          'button:not([disabled])',
          'a[href]:not([aria-disabled="true"])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(','),
      );

      (focusable ?? menu).focus?.({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  const onMenuClick = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;

    if (t.closest?.("[data-keep-open='true']")) return;

    const interactive = t.closest?.("a[href],button,[role='menuitem']");
    if (interactive) closeMenu({ restoreFocus: false });
  };

  const normalizedTriggerAriaLabel = (triggerAriaLabel ?? '').trim();
  const normalizedMenuAriaLabel = (menuAriaLabel ?? '').trim();

  const triggerLabel = normalizedTriggerAriaLabel || (primary ? `Open user menu for ${primary}` : 'Open user menu');
  const menuLabel = normalizedMenuAriaLabel || 'User menu';

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      openInteractionRef.current = 'keyboard';
      if (!open) {
        e.preventDefault();
        openMenu('keyboard');
      }
    }
  };

  const triggerButton = (
    <button
      ref={triggerRef}
      type="button"
      data-state={open ? 'open' : 'closed'}
      className={cx(
        'inline-flex items-center justify-center',
        'h-10 w-10 rounded-2xl p-0',
        'transition-colors',
        'hover:bg-primary_hover',
        open ? 'bg-primary_hover' : '',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
        'focus-visible:ring-offset-primary',
        triggerClassName,
      )}
      aria-label={triggerLabel}
      aria-haspopup="menu"
      aria-controls={hasMounted ? menuId : undefined}
      aria-expanded={open}
      onPointerDown={() => {
        openInteractionRef.current = 'pointer';
      }}
      onKeyDown={onTriggerKeyDown}
      onClick={() => {
        if (open) closeMenu({ restoreFocus: openInteractionRef.current === 'keyboard' });
        else openMenu(openInteractionRef.current);
      }}
    >
      <Avatar size={avatarSize} alt="" src={avatarSrc} fallback={primary} className="rounded-2xl" />
    </button>
  );

  const menuPanel = renderMenu ? (
    <div
      ref={menuRef}
      role="menu"
      aria-orientation="vertical"
      id={menuId}
      aria-label={menuLabel}
      aria-hidden={!open}
      tabIndex={-1}
      style={{
        position: 'fixed',
        top: position?.top ?? 0,
        left: position?.left ?? -9999,
        willChange: 'transform, opacity',
      }}
      className={cx(
        'z-[100]',
        'w-56',
        'overflow-hidden rounded-xl',
        'border border-secondary_alt',
        'bg-primary',
        'p-4',
        'transform-gpu',
        'motion-safe:transition-[opacity,transform] motion-safe:duration-200 motion-safe:ease-out',
        'motion-reduce:transition-none',
        position?.placement === 'left' ? 'origin-bottom-right' : 'origin-bottom-left',
        isVisible && open
          ? 'opacity-100 translate-x-0 scale-100'
          : cx('opacity-0 scale-95 pointer-events-none', position?.placement === 'left' ? 'translate-x-2' : '-translate-x-2'),
        menuClassName,
      )}
      onClick={onMenuClick}
    >
      <div className="px-3 py-2">
        <Text as="div" size="text-sm" tone="primary" className="truncate font-semibold" title={primary}>
          {primary}
        </Text>

        {secondary ? (
          <Text as="div" size="text-sm" tone="tertiary" className="truncate" title={secondary}>
            {secondary}
          </Text>
        ) : null}
      </div>

      <div aria-hidden="true" className="my-1 h-px bg-secondary_alt/60" />

      <div className="flex flex-col">{children}</div>
    </div>
  ) : null;

  return (
    <div className={cx('relative', className)}>
      {!open && derivedTooltip ? (
        <Tooltip content={derivedTooltip} side="right" portal>
          {triggerButton}
        </Tooltip>
      ) : (
        triggerButton
      )}

      {hasMounted && menuPanel ? createPortal(menuPanel, document.body) : null}
    </div>
  );
}

NavUserMenu.displayName = 'NavUserMenu';
